// Gestion des Commandes - Espace Vendeur
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingCart, Search, Filter, Eye, Truck, CheckCircle2, Clock,
  XCircle, Package, ChevronRight, Download, MessageSquare, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Statuts des commandes
const ORDER_STATUSES = {
  pending: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'En attente', icon: Clock },
  confirmed: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Confirmée', icon: CheckCircle2 },
  processing: { color: 'bg-indigo-100 text-indigo-700 border-indigo-200', label: 'En préparation', icon: Package },
  shipped: { color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Expédiée', icon: Truck },
  delivered: { color: 'bg-green-100 text-green-700 border-green-200', label: 'Livrée', icon: CheckCircle2 },
  cancelled: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Annulée', icon: XCircle },
};

// Carte commande
function OrderCard({ order, onViewDetails, onUpdateStatus }) {
  const status = ORDER_STATUSES[order.status] || ORDER_STATUSES.pending;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all"
    >
      <div className="flex items-start gap-4">
        {/* Image produit */}
        <div className="w-16 h-16 rounded-lg bg-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden">
          {order.items[0]?.image ? (
            <img src={order.items[0].image} alt="" className="w-full h-full object-cover" />
          ) : (
            <Package size={24} className="text-gray-300" />
          )}
        </div>

        {/* Infos commande */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div>
              <p className="font-bold text-gray-900">#{order.id}</p>
              <p className="text-sm text-gray-500">{order.date}</p>
            </div>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${status.color}`}>
              <StatusIcon size={12} />
              {status.label}
            </span>
          </div>

          <p className="text-sm text-gray-700 mb-2 line-clamp-1">
            {order.items.map(i => i.title).join(', ')}
          </p>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Client: <span className="font-medium text-gray-700">{order.customer.name}</span></p>
              <p className="text-lg font-bold text-gray-900">{order.total.toFixed(2)} €</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewDetails(order)}
          className="flex-1 rounded-lg"
        >
          <Eye size={14} className="mr-1" /> Détails
        </Button>
        {order.status === 'pending' && (
          <Button
            size="sm"
            onClick={() => onUpdateStatus(order, 'confirmed')}
            className="flex-1 bg-blue-500 hover:bg-blue-600 rounded-lg"
          >
            <CheckCircle2 size={14} className="mr-1" /> Confirmer
          </Button>
        )}
        {order.status === 'confirmed' && (
          <Button
            size="sm"
            onClick={() => onUpdateStatus(order, 'processing')}
            className="flex-1 bg-indigo-500 hover:bg-indigo-600 rounded-lg"
          >
            <Package size={14} className="mr-1" /> Préparer
          </Button>
        )}
        {order.status === 'processing' && (
          <Button
            size="sm"
            onClick={() => onUpdateStatus(order, 'shipped')}
            className="flex-1 bg-purple-500 hover:bg-purple-600 rounded-lg"
          >
            <Truck size={14} className="mr-1" /> Expédier
          </Button>
        )}
      </div>
    </motion.div>
  );
}

// Modal détails commande
function OrderDetailModal({ order, isOpen, onClose, onUpdateStatus }) {
  if (!isOpen || !order) return null;

  const status = ORDER_STATUSES[order.status] || ORDER_STATUSES.pending;
  const StatusIcon = status.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Commande #{order.id}</h2>
                <p className="text-sm text-gray-500">{order.date}</p>
              </div>
              <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border ${status.color}`}>
                <StatusIcon size={14} />
                {status.label}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Client */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Informations client</h3>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-medium text-gray-900">{order.customer.name}</p>
                <p className="text-sm text-gray-600">{order.customer.email}</p>
                <p className="text-sm text-gray-600">{order.customer.phone}</p>
              </div>
            </div>

            {/* Adresse livraison */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Adresse de livraison</h3>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-700">{order.shipping.address}</p>
                <p className="text-sm text-gray-700">{order.shipping.postalCode} {order.shipping.city}</p>
              </div>
            </div>

            {/* Produits */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Articles commandés</h3>
              <div className="space-y-3">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                    <div className="w-14 h-14 rounded-lg bg-white flex items-center justify-center">
                      {item.image ? (
                        <img src={item.image} alt="" className="w-12 h-12 object-contain" />
                      ) : (
                        <Package size={20} className="text-gray-300" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.title}</p>
                      <p className="text-sm text-gray-500">Qté: {item.quantity}</p>
                    </div>
                    <p className="font-bold text-gray-900">{(item.price * item.quantity).toFixed(2)} €</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Total */}
            <div className="bg-orange-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">Sous-total</span>
                <span className="font-medium">{(order.total - order.shipping.cost).toFixed(2)} €</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">Livraison</span>
                <span className="font-medium">{order.shipping.cost.toFixed(2)} €</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-orange-200">
                <span className="font-bold text-gray-900">Total</span>
                <span className="text-xl font-bold text-orange-600">{order.total.toFixed(2)} €</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-100 flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">
              Fermer
            </Button>
            <Button 
              onClick={() => window.print()}
              variant="outline"
              className="rounded-xl"
            >
              <Download size={16} className="mr-2" /> Facture
            </Button>
            {order.status !== 'delivered' && order.status !== 'cancelled' && (
              <Button 
                className="bg-orange-500 hover:bg-orange-600 rounded-xl"
                onClick={() => {
                  const nextStatus = {
                    pending: 'confirmed',
                    confirmed: 'processing',
                    processing: 'shipped',
                    shipped: 'delivered'
                  };
                  onUpdateStatus(order, nextStatus[order.status]);
                  onClose();
                }}
              >
                Passer à l'étape suivante
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function CommandesPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Données simulées
      setOrders([
        {
          id: 'CMD-2024-001',
          date: '15 Jan 2025, 14:32',
          status: 'pending',
          customer: { name: 'Marie Lambert', email: 'marie@example.com', phone: '06 12 34 56 78' },
          items: [
            { title: 'Perceuse Bosch Professional', quantity: 1, price: 149, image: null }
          ],
          shipping: { address: '12 rue de la Paix', postalCode: '75002', city: 'Paris', cost: 5.90 },
          total: 154.90
        },
        {
          id: 'CMD-2024-002',
          date: '14 Jan 2025, 09:15',
          status: 'confirmed',
          customer: { name: 'Pierre Durand', email: 'pierre@example.com', phone: '06 98 76 54 32' },
          items: [
            { title: 'Lot de 50 chevilles', quantity: 2, price: 24, image: null },
            { title: 'Vis inox 4x40mm (100pcs)', quantity: 1, price: 12, image: null }
          ],
          shipping: { address: '45 avenue Victor Hugo', postalCode: '69003', city: 'Lyon', cost: 4.90 },
          total: 64.90
        },
        {
          id: 'CMD-2024-003',
          date: '13 Jan 2025, 16:45',
          status: 'processing',
          customer: { name: 'Sophie Martin', email: 'sophie@example.com', phone: '06 55 44 33 22' },
          items: [
            { title: 'Niveau laser Makita', quantity: 1, price: 289, image: null }
          ],
          shipping: { address: '8 place Bellecour', postalCode: '69002', city: 'Lyon', cost: 0 },
          total: 289
        },
        {
          id: 'CMD-2024-004',
          date: '12 Jan 2025, 11:20',
          status: 'shipped',
          customer: { name: 'Jean Petit', email: 'jean@example.com', phone: '06 11 22 33 44' },
          items: [
            { title: 'Coffret tournevis Facom', quantity: 1, price: 89, image: null }
          ],
          shipping: { address: '23 rue Gambetta', postalCode: '33000', city: 'Bordeaux', cost: 6.90 },
          total: 95.90
        },
        {
          id: 'CMD-2024-005',
          date: '10 Jan 2025, 08:00',
          status: 'delivered',
          customer: { name: 'Lucie Bernard', email: 'lucie@example.com', phone: '06 77 88 99 00' },
          items: [
            { title: 'Marteau Peugeot 500g', quantity: 3, price: 19, image: null }
          ],
          shipping: { address: '5 allée des Roses', postalCode: '31000', city: 'Toulouse', cost: 4.50 },
          total: 61.50
        },
      ]);
    } catch (e) {
      console.error('Error fetching orders:', e);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = (order, newStatus) => {
    setOrders(orders.map(o => 
      o.id === order.id ? { ...o, status: newStatus } : o
    ));
    toast.success(`Commande ${order.id} mise à jour`);
  };

  const filteredOrders = orders.filter(o => {
    const matchesSearch = o.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          o.customer.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || o.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats rapides
  const stats = {
    pending: orders.filter(o => o.status === 'pending').length,
    processing: orders.filter(o => o.status === 'processing' || o.status === 'confirmed').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="commandes-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Commandes</h1>
        <p className="text-gray-500">Gère tes commandes et suis les livraisons</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Clock size={24} className="text-yellow-600" />
            <div>
              <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
              <p className="text-sm text-yellow-600">En attente</p>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Package size={24} className="text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-blue-700">{stats.processing}</p>
              <p className="text-sm text-blue-600">En préparation</p>
            </div>
          </div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Truck size={24} className="text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-purple-700">{stats.shipped}</p>
              <p className="text-sm text-purple-600">Expédiées</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert si commandes en attente */}
      {stats.pending > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <AlertCircle size={20} className="text-orange-600 flex-shrink-0" />
          <p className="text-sm text-orange-700">
            <span className="font-semibold">{stats.pending} commande(s)</span> nécessite(nt) ton attention
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Rechercher une commande..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm"
        >
          <option value="all">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="confirmed">Confirmées</option>
          <option value="processing">En préparation</option>
          <option value="shipped">Expédiées</option>
          <option value="delivered">Livrées</option>
          <option value="cancelled">Annulées</option>
        </select>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <ShoppingCart size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="font-bold text-gray-900 mb-2">Aucune commande</h3>
          <p className="text-gray-500">Tes commandes apparaîtront ici</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filteredOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onViewDetails={setSelectedOrder}
              onUpdateStatus={updateOrderStatus}
            />
          ))}
        </div>
      )}

      {/* Order Detail Modal */}
      <OrderDetailModal
        order={selectedOrder}
        isOpen={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onUpdateStatus={updateOrderStatus}
      />
    </div>
  );
}
